import { Clear } from '@mui/icons-material';
import { FormControl, IconButton, InputLabel, MenuItem, Select, Stack, TextField } from '@mui/material';
import { useId, useLayoutEffect, useRef } from 'react';

type CronExpressionInputProps = {
	value?: string | null;
	onValueChange: (value: string | null) => void;
};

type CronPreset = {
	label: string;
	value: string;
};

const CRON_PRESETS: CronPreset[] = [
	{ label: 'Every 5 minutes', value: '*/5 * * * *' },
	{ label: 'Every 30 minutes', value: '*/30 * * * *' },
	{ label: 'Hourly', value: '0 * * * *' },
	{ label: 'Every 2 hours', value: '0 */2 * * *' },
	{ label: 'Every 4 hours', value: '0 */4 * * *' },
	{ label: 'Daily at 1 PM UTC', value: '0 13 * * *' },
	{ label: 'Weekdays at 9 AM UTC', value: '0 9 * * MON-FRI' },
	{ label: 'Monday at 1 PM UTC', value: '0 13 * * MON' },
	{ label: 'Tuesday at 1 PM UTC', value: '0 13 * * TUE' },
	{ label: 'Wednesday at 1 PM UTC', value: '0 13 * * WED' },
	{ label: 'Thursday at 1 PM UTC', value: '0 13 * * THU' },
	{ label: 'Friday at 1 PM UTC', value: '0 13 * * FRI' },
	{ label: 'Saturday at 1 PM UTC', value: '0 13 * * SAT' },
	{ label: 'Sunday at 1 PM UTC', value: '0 13 * * SUN' },
	{ label: 'Monthly on day 1', value: '0 0 1 * *' },
	{ label: 'Yearly', value: '0 0 1 JAN *' },
];

const CRON_PART_RANGES = [
	{ min: 0, max: 59 },
	{ min: 0, max: 23 },
	{ min: 1, max: 31 },
	{ min: 1, max: 12, aliases: ['JAN', 'FEB', 'MAR', 'APR', 'MAY', 'JUN', 'JUL', 'AUG', 'SEP', 'OCT', 'NOV', 'DEC'] },
	{ min: 0, max: 7, aliases: ['SUN', 'MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT'] },
];

const CLEAR_BUTTON_SX = {
	width: 24,
	height: 24,
	color: 'action.disabled',
	'&:hover': {
		color: 'action.active',
		backgroundColor: 'transparent',
	},
	'&.Mui-disabled': {
		visibility: 'hidden',
	},
};

function isValidCronExpression(value: string): boolean {
	const parts = value.trim().split(/\s+/);
	if (parts.length !== 5) {
		return false;
	}

	return parts.every((part, index) => isValidCronPart(part, CRON_PART_RANGES[index]));
}

function isValidCronPart(part: string, range: { min: number; max: number; aliases?: string[] }): boolean {
	if (!part || /[?L#]/i.test(part)) {
		return false;
	}

	return part.split(',').every((segment) => isValidCronSegment(segment, range));
}

function isValidCronSegment(segment: string, range: { min: number; max: number; aliases?: string[] }): boolean {
	const [base, step, ...rest] = segment.split('/');
	if (rest.length > 0 || !base) {
		return false;
	}

	if (step !== undefined && (!/^\d+$/.test(step) || Number(step) < 1)) {
		return false;
	}

	if (base === '*') {
		return true;
	}

	const rangeParts = base.split('-');
	if (rangeParts.length > 2) {
		return false;
	}

	const start = parseCronValue(rangeParts[0], range);
	const end = rangeParts.length === 2 ? parseCronValue(rangeParts[1], range) : start;

	return start !== null && end !== null && start <= end;
}

function parseCronValue(value: string, range: { min: number; max: number; aliases?: string[] }): number | null {
	const aliasIndex = range.aliases?.findIndex((alias) => alias.toUpperCase() === value.toUpperCase()) ?? -1;
	if (aliasIndex >= 0) {
		const aliasValue = range.aliases?.[aliasIndex];
		if (aliasValue === 'SUN' && range.max === 7) {
			return 0;
		}

		return range.min + aliasIndex;
	}

	if (!/^\d+$/.test(value)) {
		return null;
	}

	const parsed = Number(value);
	return parsed >= range.min && parsed <= range.max ? parsed : null;
}

export default function CronExpressionInput({ value, onValueChange }: CronExpressionInputProps) {
	const presetLabelId = useId();
	const inputRef = useRef<HTMLInputElement | null>(null);
	const selectionRef = useRef<{ start: number; end: number; direction: 'forward' | 'backward' | 'none' | undefined } | null>(null);
	const cron = value ?? '';
	const selectedPreset = CRON_PRESETS.some((preset) => preset.value === cron) ? cron : '';
	const hasValidationError = cron.trim().length > 0 && !isValidCronExpression(cron);

	useLayoutEffect(() => {
		const input = inputRef.current;
		const selection = selectionRef.current;
		if (!input || !selection || document.activeElement !== input) {
			return;
		}

		const start = Math.min(selection.start, input.value.length);
		const end = Math.min(selection.end, input.value.length);
		input.setSelectionRange(start, end, selection.direction);
		selectionRef.current = null;
	}, [cron]);

	return (
		<Stack spacing={1}>
			<FormControl variant="standard" fullWidth>
				<InputLabel id={presetLabelId}>Preset</InputLabel>
				<Select
					labelId={presetLabelId}
					value={selectedPreset}
					label="Preset"
					onChange={(event) => onValueChange(event.target.value || null)}
				>
					<MenuItem value="">
						<em>Custom</em>
					</MenuItem>
					{CRON_PRESETS.map((preset) => (
						<MenuItem key={preset.value} value={preset.value}>
							{preset.label}
						</MenuItem>
					))}
				</Select>
			</FormControl>
			<Stack direction="row" spacing={0.5} alignItems="flex-end">
				<TextField
					label="Expression"
					variant="standard"
					value={cron}
					onChange={(event) => {
						selectionRef.current = {
							start: event.target.selectionStart ?? event.target.value.length,
							end: event.target.selectionEnd ?? event.target.value.length,
							direction: event.target.selectionDirection ?? undefined,
						};
						onValueChange(event.target.value || null);
					}}
					inputRef={inputRef}
					error={hasValidationError}
					helperText={hasValidationError ? 'Use five fields without Quartz-only tokens.' : undefined}
					required
					fullWidth
				/>
				<IconButton size="small" title="Clear" onClick={() => onValueChange(null)} disabled={!cron} sx={CLEAR_BUTTON_SX}>
					<Clear fontSize="small" />
				</IconButton>
			</Stack>
		</Stack>
	);
}
